"use client";

import {
  createContext,
  useContext,
  useReducer,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { logger } from "@/lib/infra/logger";
import { authClient } from "@/lib/auth/client";
import { obfuscateCart, deobfuscateCart } from "@/lib/shared/utils";
import { getStock, type StockValue } from "@/lib/commerce/stock";
import { getEffectivePurchaseLimit } from "@/lib/commerce/limits";

interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
  size?: string;
  stock?: StockValue;
  is_preorder?: boolean;
  purchase_limit?: number | null;
  preorder_limit?: number | null;
}

export const CART_ITEM_HARD_CAP = 10;

export function getCartItemMaxQuantity(item: CartItem): number {
  if (item.stock === undefined) return CART_ITEM_HARD_CAP;


  const isPreorder = item.is_preorder === true || item.preorder_limit != null;
  if (isPreorder) {
    return (
      item.preorder_limit ??
      getEffectivePurchaseLimit(item.purchase_limit) ??
      CART_ITEM_HARD_CAP
    );
  }

  const stockCap = getStock(item.stock, item.size) || 0;
  const limitCap = getEffectivePurchaseLimit(item.purchase_limit) ?? CART_ITEM_HARD_CAP;
  return Math.min(stockCap, limitCap, CART_ITEM_HARD_CAP);
}

type CartState = CartItem[];

type CartAction =
  | {
      type: "ADD_ITEM";
      payload: Omit<CartItem, "quantity"> & { quantity: number };
    }
  | {
      type: "UPDATE_QUANTITY";
      payload: { id: number; size?: string; quantity: number };
    }
  | { type: "REMOVE_ITEM"; payload: { id: number; size?: string } }
  | { type: "CLEAR_CART" }
  | { type: "HYDRATE"; payload: CartState };

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD_ITEM": {
      const existingItemIndex = state.findIndex(
        (item) => item.id === action.payload.id && item.size === action.payload.size
      );
      if (existingItemIndex >= 0) {
        const updated = [...state];
        const existingItem = updated[existingItemIndex];
        if (existingItem) {
          const newQuantity = existingItem.quantity + action.payload.quantity;
          updated[existingItemIndex] = {
            ...existingItem,
            quantity: Math.min(newQuantity, getCartItemMaxQuantity(existingItem)),
          };
          return updated;
        }
      }
  const freshItem: CartItem = { ...(action.payload as CartItem) };
  const quantity = Number.isFinite(freshItem.quantity) ? freshItem.quantity : 1;
  return [
    ...state,
    { ...action.payload, quantity: Math.min(quantity, getCartItemMaxQuantity(freshItem)) },
  ];
    }
    case "UPDATE_QUANTITY": {
      return state
        .map((item) =>
          item.id === action.payload.id && item.size === action.payload.size
            ? { ...item, quantity: Math.min(action.payload.quantity, getCartItemMaxQuantity(item)) }
            : item
        )
        .filter((item) => item.quantity > 0);
    }
    case "REMOVE_ITEM": {
      return state.filter(
        (item) => !(item.id === action.payload.id && item.size === action.payload.size)
      );
    }
    case "CLEAR_CART": {
      return [];
    }
    case "HYDRATE": {
      return action.payload;
    }
    default: {
      return state;
    }
  }
}

interface CartActions {
  addItem: (item: Omit<CartItem, "quantity"> & { quantity: number }) => void;
  updateQuantity: (id: number, size: string | undefined, quantity: number) => void;
  removeItem: (id: number, size: string | undefined) => void;
  clearCart: () => void;
}

interface CartStateValue {
  cart: CartState;
  total: number;
}

const CartActionsContext = createContext<CartActions | undefined>(undefined);
const CartStateContext = createContext<CartStateValue | undefined>(undefined);
const CartRefContext = createContext<{ current: CartState } | undefined>(undefined);

export function useCartRef() {
  const ref = useContext(CartRefContext);
  if (!ref) {
    throw new Error("useCartRef must be used within CartProvider");
  }
  return ref;
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, dispatch] = useReducer(cartReducer, []);
  const [hydrated, setHydrated] = useState(false);
  const cartRef = useRef<CartState>(cart);
  cartRef.current = cart;

  useEffect(() => {
    try {
      const saved = localStorage.getItem("cart");
      if (saved) {
        const deobfuscated = deobfuscateCart(saved);
        if (Array.isArray(deobfuscated)) {
          // Deduplicate by id+size to prevent doubling on reload after previous buggy saves
          const deduped = new Map<string, CartItem>();
          for (const item of deobfuscated as CartItem[]) {
            const key = `${item.id}-${item.size ?? ""}`;
            const existing = deduped.get(key);
            if (existing) {
              existing.quantity = Math.min(existing.quantity + (item.quantity || 0), getCartItemMaxQuantity(existing));
            } else {
              deduped.set(key, { ...item });
            }
          }
          dispatch({ type: "HYDRATE", payload: Array.from(deduped.values()) });
        }
      }
    } catch (error) {
      logger.error("Failed to load cart from localStorage:", error);
    }
    setHydrated(true);
  }, []);

  const { data: session } = authClient.useSession();

  useEffect(() => {
    if (!hydrated) return;
    const br = localStorage.getItem("browser_id") || (() => {
      const id = `br_${Math.random().toString(36).slice(2,10)}`;
      localStorage.setItem("browser_id", id);
      document.cookie = `br=${id}; path=/; max-age=${60*60*24*365}`;
      return id;
    })();
    fetch("/api/cart", { headers: { "x-browser-id": br } })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data.cart) && data.cart.length > 0 && cartRef.current.length === 0) {
          const local = cartRef.current;
          const merged = [...local, ...data.cart] as CartItem[];
          const deduped = new Map<string, CartItem>();
          for (const item of merged) {
            const key = `${item.id}-${item.size ?? ""}`;
            const existing = deduped.get(key);
            if (existing) existing.quantity = Math.min(existing.quantity + (item.quantity || 0), getCartItemMaxQuantity(existing));
            else deduped.set(key, { ...item });
          }
          if (deduped.size > 0) dispatch({ type: "HYDRATE", payload: Array.from(deduped.values()) });
        }
      })
      .catch(() => {});
  }, [hydrated]);

  useEffect(() => {
    if (!hydrated) return;

    const timeoutId = setTimeout(() => {
      try {
        const obfuscated = obfuscateCart(cart);
        localStorage.setItem("cart", obfuscated);
        const br = localStorage.getItem("browser_id") || "";
        fetch("/api/cart", {
          method: "PUT",
          headers: { "Content-Type": "application/json", "x-browser-id": br },
          body: JSON.stringify({ cart: cart.map(({ id, quantity, size }) => ({ id, quantity, size })), browserId: br }),
        }).catch(() => {});
      } catch (error) {
        logger.error("Failed to save cart to localStorage:", error);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [cart, hydrated]);

  // Behavioral automation: abandoned-cart heartbeat (lightweight ping)
  useEffect(() => {
    if (!hydrated || cart.length === 0) return;
    const email = session?.user?.email;
    if (!email) return;
    const timer = setTimeout(() => {
      fetch("/api/cart/heartbeat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, cart }),
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, [cart, hydrated, session?.user?.email]);

  const addItem = useCallback((item: Omit<CartItem, "quantity"> & { quantity: number }) => {
    dispatch({ type: "ADD_ITEM", payload: item });
  }, []);

  const updateQuantity = useCallback((id: number, size: string | undefined, quantity: number) => {
    dispatch({ type: "UPDATE_QUANTITY", payload: { id, size, quantity } });
  }, []);

  const removeItem = useCallback((id: number, size: string | undefined) => {
    dispatch({ type: "REMOVE_ITEM", payload: { id, size } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: "CLEAR_CART" });
  }, []);

  const total = useMemo(
    () => cart.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0),
    [cart]
  );

  const actions = useMemo(
    () => ({ addItem, updateQuantity, removeItem, clearCart }),
    [addItem, updateQuantity, removeItem, clearCart]
  );

  const state = useMemo(() => ({ cart, total }), [cart, total]);

  return (
    <CartActionsContext.Provider value={actions}>
      <CartStateContext.Provider value={state}>
        <CartRefContext.Provider value={cartRef}>{children}</CartRefContext.Provider>
      </CartStateContext.Provider>
    </CartActionsContext.Provider>
  );
}

export const useCartActions = () => {
  const context = useContext(CartActionsContext);
  if (!context) {
    throw new Error("useCartActions must be used within CartProvider");
  }
  return context;
};

export const useCartState = () => {
  const context = useContext(CartStateContext);
  if (!context) {
    throw new Error("useCartState must be used within CartProvider");
  }
  return context;
};

export const useCart = () => {
  const actions = useContext(CartActionsContext);
  const state = useContext(CartStateContext);
  if (!actions || !state) {
    throw new Error("useCart must be used within CartProvider");
  }
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
};
